import { Component, ElementRef, ViewChild } from '@angular/core';
import { RadioControl } from '../../../create-request/models/form/FormControlRadio';
import { Store } from '@ngrx/store';
import { Subject, take, takeUntil } from 'rxjs';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import * as InitReleaseActions from '../../actions/init-release.actions';
import { createControl } from '../../../create-request/utilities/formUtilities';
import { TextInputModel } from '../../../shared/components/controls/text-input-control/TextInputModel';
import { createRuleInRConstants } from '../../../shared/utilities/Constants';
import { ValidateInputService } from '../../../shared/services/validate-input.service';
import { amountAllowedChars } from '../../../shared/utilities/regex-patterns';
import { amountFormatter } from '../../../shared/utilities/AmountFormatter';
import { selectCallCustomRulesPostFailed, selectCallCustomRulesPostSuccess } from '../../reducers';
import { ToastrService } from 'ngx-toastr';

@Component({
  standalone: false,
    selector: 'mm-init-release-create-rule-modal',
    templateUrl: './init-release-create-rule-modal.component.html',
    styleUrls: ['./init-release-create-rule-modal.component.scss']
})
export class InitReleaseCreateRuleModalComponent {
    @ViewChild('createRuleModal') public createRuleModal: ElementRef;
    destroy$: Subject<boolean> = new Subject<boolean>();
    createRuleModalFormGroup: FormGroup = new FormGroup({});
    bannerErrorText: string = ''
    showErrorBanner: boolean = false;

    createRuleTransactionTypeFormGroup: FormGroup = new FormGroup({});
    createRulePartyFormGroup: FormGroup = new FormGroup({});
    createRuleAuthorizationFormGroup: FormGroup = new FormGroup({});
    
    createRuleCheck = new FormControl(false)
    createRuleAch = new FormControl(false)
    createRuleIt = new FormControl(false)
    createRuleWire = new FormControl(false)
    createRuleAllTransactionType = new FormControl(false)

    createRuleFirstParty = new FormControl(false)
    createRuleThirdParty = new FormControl(false)
    createRuleAllParty = new FormControl(false)

    createRuleNoAuthRequired = new FormControl(false)
    createRuleOneTimeAuthRequired = new FormControl(false)
    createRuleStandingAuth = new FormControl(false)
    createRuleAllAuth = new FormControl(false)

    createRuleConstants = createRuleInRConstants;
    amountAllowedChars = amountAllowedChars;
    transactionTypeHasError: boolean = false;
    partyHasError: boolean = false;
    authorizationHasError: boolean = false;
    ruleNameHasError:boolean = false;
    ruleAmountHasError: boolean = false;
    modalInputsHasErrors: boolean = false;
    errorOnCreateRule: boolean = false;
    
    ruleTypes: 'Transaction' | 'Standing-Authorization' | '';
    ruleNameModel: TextInputModel = {
        controlName: 'ruleName',
        label: 'Rule Name',
        hasGreyText: false,
        htmlMaxLength: 25,
    };
    ruleAmountModel: TextInputModel = {
        controlName: 'ruleAmount',
        label: 'For amount greater than:',
        hasGreyText: false,
    };
    ruleTypeSelectorModel: RadioControl = {
        title: 'Rule type',
        controlName: 'ruleType',
        radios: [
            {
                value: 'Transaction',
                label: ['Transaction'],
            },
            {
                value: 'Standing-Authorization',
                label: ['Standing Authorization'],
            },
        ],
        validationMessage: 'Select a Rule Type'
    };

    ngOnInit(): void {
        this.addControlsToFormGroup();
        this.updateStoreWhenUserUpdatesForm();
        this.handleAllCheckboxSelected();
        this.clearValuesOnRuleTypeChanged();

    }

    clearValuesOnRuleTypeChanged(){
        this.createRuleModalFormGroup.get('ruleType')?.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
            this.clearCustomRuleFormValues(false,false);
            });
    }

    formatAmount(): void {
        let amount = this.createRuleModalFormGroup.get('ruleAmount');
        if (amount && amount.valid) {
            let temp = Number(amount.value.replace(/[^0-9.]/g, ''));
            this.createRuleModalFormGroup.get('ruleAmount')?.setValue(amountFormatter.format(temp));
        }
    }

    validateOnKeyDown(event: any) {
        const input = event.target.value;
        const key = event.key;
        if (['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End'].includes(key)) return;
        if (key === 'Enter' || key === 'Tab') {
            this.formatAmount();
            return;
        }
        if (key === '.' && input.includes('.')) {
            event.preventDefault();
            return;
        }
        if (!this.validationService.validateNumberInput(event, amountAllowedChars)) {
            event.preventDefault();
            return;
        }
    }

    get selectedRuleType(): string | undefined {
        return this.createRuleModalFormGroup.get('ruleType')?.value;
    }

    get selectedRuleName(): string | undefined {
        return this.createRuleModalFormGroup.get('ruleName')?.value;
    }

    get isTransactionRuleType(): boolean {
        return this.selectedRuleType === 'Transaction';
    }

    get isStandingAuthorizationRuleType(): boolean {
        return this.selectedRuleType === 'Standing-Authorization';
    }

    onModalCreateRuleClick(): void {
        this.errorOnCreateRule = false;
        this.modalInputsHasErrors = this.showErrorBanner = this.validateModalInputs();
        if(!this.modalInputsHasErrors){
            this.store.dispatch(InitReleaseActions.callCreateCustomRulesPost());
            this.store.select(selectCallCustomRulesPostSuccess).pipe(take(2)).subscribe(success => {
                if (success) {
                    this.store.dispatch(InitReleaseActions.callCreateCustomRulesGet());
                    this.toastr.success('Rule Created');
                    this.createRuleModal.nativeElement.hideModal();
                }
            });
            this.store.select(selectCallCustomRulesPostFailed).pipe(takeUntil(this.destroy$)).subscribe(failure => {
                if (failure) {
                    this.errorOnCreateRule = true;
                }
            });
        }
    }

    validateModalInputs(): boolean {
        this.transactionTypeHasError = this.isTransactionRuleType &&
            !Object.values(this.createRuleTransactionTypeFormGroup.controls).some(control => control.value);

        this.partyHasError = !Object.values(this.createRulePartyFormGroup.controls).some(control => control.value);

        this.authorizationHasError = this.isTransactionRuleType &&
            !Object.values(this.createRuleAuthorizationFormGroup.controls).some(control => control.value);
        
        const ruleAmountControl = this.createRuleModalFormGroup.get('ruleAmount');
        this.ruleAmountHasError = (!ruleAmountControl?.value || ruleAmountControl.value.trim() === '')  && this.isTransactionRuleType;

        if (this.ruleAmountHasError) {
            ruleAmountControl?.markAsDirty();
            ruleAmountControl?.setErrors({ invalid: true });
        }

        const ruleNameControl = this.createRuleModalFormGroup.get('ruleName');
        this.ruleNameHasError = (!ruleNameControl?.value || ruleNameControl.value.trim() === '');

        if (this.ruleNameHasError) {
            ruleNameControl?.markAsDirty();
            ruleNameControl?.setErrors({ invalid: true });
        }

        this.setErrorBannerText()

        return this.transactionTypeHasError || this.partyHasError || this.authorizationHasError || this.ruleNameHasError || this.ruleAmountHasError;
    }

    setErrorBannerText() {
        const errors: string[] = [];
        this.bannerErrorText = '';
        if (this.ruleNameHasError && this.ruleAmountHasError) errors.push('Enter rule name and amount')
        if (this.ruleNameHasError && !this.ruleAmountHasError) errors.push('Enter rule name');
        if (this.ruleAmountHasError && !this.ruleNameHasError) errors.push('Enter an amount');
        if (errors.length) errors[errors.length - 1] += ' to create rule.';

        if ([this.transactionTypeHasError, this.partyHasError, this.authorizationHasError].filter(Boolean).length > 1) {
            errors.push('At least one option must be selected in each section to create rule.');
        } else if (this.transactionTypeHasError || this.partyHasError || this.authorizationHasError) {
            errors.push('At least one option must be selected to create rule.');
        }

        this.bannerErrorText = errors.join('\n');
    }

    onCancelClick(): void {
        this.createRuleModal.nativeElement.hideModal();
        this.clearCustomRuleFormValues(true,true)
    }

    onCreateRuleButtonClickedEvent(): void {
        this.clearCustomRuleFormValues(true,true);
        this.createRuleModal.nativeElement.showModal();
    }

    addControlsToFormGroup(): void {
        createControl(this.createRuleModalFormGroup, 'ruleType', this.ruleTypes, [Validators.required]);
        createControl(this.createRuleModalFormGroup, 'ruleName', '', [Validators.maxLength(Number(this.ruleNameModel.htmlMaxLength))]);
        createControl(this.createRuleModalFormGroup, 'ruleAmount', '',[]);

        this.createRuleModalFormGroup.addControl('createRuleTransactionType', this.createRuleTransactionTypeFormGroup);
        this.createRuleModalFormGroup.addControl('createRuleParty', this.createRulePartyFormGroup);
        this.createRuleModalFormGroup.addControl('createRuleAuthorization', this.createRuleAuthorizationFormGroup);

        const addControls = (group: FormGroup, controls: { [key: string]: FormControl }) => {
            Object.entries(controls).forEach(([key, control]) => group.addControl(key, control));
        };

        addControls(this.createRuleTransactionTypeFormGroup, {
            createRuleCheck: this.createRuleCheck,
            createRuleAch: this.createRuleAch,
            createRuleIt: this.createRuleIt,
            createRuleWire: this.createRuleWire,
            createRuleAllTransactionType: this.createRuleAllTransactionType
        });
        addControls(this.createRulePartyFormGroup, {
            createRuleFirstParty: this.createRuleFirstParty,
            createRuleThirdParty: this.createRuleThirdParty,
            createRuleAllParty: this.createRuleAllParty
        });
        addControls(this.createRuleAuthorizationFormGroup, {
            createRuleNoAuthRequired: this.createRuleNoAuthRequired,
            createRuleOneTimeAuthRequired: this.createRuleOneTimeAuthRequired,
            createRuleStandingAuth: this.createRuleStandingAuth,
            createRuleAllAuth: this.createRuleAllAuth
        });
        this.createRuleModalFormGroup.get('ruleAmount')?.setValue('$0.00');
        this.createRuleModalFormGroup.get('ruleType')?.setValue('Transaction');
    }

    clearCustomRuleFormValues(resetRuleType: boolean, resetRuleName: boolean) {
        this.createRuleModalFormGroup.reset({
            ruleAmount: '$0.00',
            ruleType: resetRuleType ? 'Transaction' : this.createRuleModalFormGroup.get('ruleType')?.value,
            ruleName: resetRuleName ? '' : this.createRuleModalFormGroup.get('ruleName')?.value
        }, { emitEvent: false });
        this.transactionTypeHasError = false;
        this.partyHasError = false;
        this.authorizationHasError = false;
        this.ruleNameHasError = false;
        this.ruleAmountHasError = false;
        this.showErrorBanner = false;
        this.bannerErrorText = '';
        this.errorOnCreateRule = false
    }

    updateStoreWhenUserUpdatesForm(): void {
        this.createRuleModalFormGroup.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(values => {
                this.store.dispatch(InitReleaseActions.saveCreateRuleValues({ createCustomRuleSection: values }));
            });
    }

    handleAllCheckboxSelected() {
        const handleAllCheckbox = (formGroup: FormGroup, allKey: string) => {
            formGroup.get(allKey)?.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe((allSelected: boolean) => {
                Object.keys(formGroup.controls).forEach(key => {
                if (key !== allKey) {
                    formGroup.get(key)?.setValue(allSelected, { emitEvent: false });
                }
                });
            });
        };

        handleAllCheckbox(this.createRuleTransactionTypeFormGroup, 'createRuleAllTransactionType');
        handleAllCheckbox(this.createRulePartyFormGroup, 'createRuleAllParty');
        handleAllCheckbox(this.createRuleAuthorizationFormGroup, 'createRuleAllAuth');

        const handleIndividualCheckboxes = (formGroup: FormGroup, allKey: string) => {
            const allControl = formGroup.get(allKey);
            Object.keys(formGroup.controls).forEach(key => {
                if (key !== allKey) {
                    formGroup.get(key)?.valueChanges
                        .pipe(takeUntil(this.destroy$))
                        .subscribe(() => {
                            const allChecked = Object.keys(formGroup.controls)
                                .filter(k => k !== allKey)
                                .every(k => formGroup.get(k)?.value === true);
                            if (allControl?.value !== allChecked) {
                                allControl?.setValue(allChecked, { emitEvent: false });
                            }
                        });
                }
            });
        };

        handleIndividualCheckboxes(this.createRuleTransactionTypeFormGroup, 'createRuleAllTransactionType');
        handleIndividualCheckboxes(this.createRulePartyFormGroup, 'createRuleAllParty');
        handleIndividualCheckboxes(this.createRuleAuthorizationFormGroup, 'createRuleAllAuth');
    }

    constructor(private store: Store, public validationService: ValidateInputService,private toastr: ToastrService) { }
}
